import React, { useEffect, useState } from "react";
import { FaRegEdit } from "react-icons/fa";
import { IoInformationCircleOutline, IoBusinessOutline } from "react-icons/io5";
import { MdOutlineAlternateEmail, MdBusinessCenter } from "react-icons/md";
import { LuPhone, LuMapPin } from "react-icons/lu";
import { CgProfile } from "react-icons/cg";
import { SlCalender } from "react-icons/sl";
import { BsExclamationCircle, BsFileText, BsPercent } from "react-icons/bs";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { GiReceiveMoney } from "react-icons/gi";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const SupplierProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { user, updateUser } = useAuth();

  const [formData, setFormData] = useState({
    fullName: "",
    emailId: "",
    mobNo: "",
    gender: "",
    address: "",
    businessName: "",
    businessType: "",
    businessRegistrationName: "",
    panNo: "",
    gstIn: "",
    commission: "",
    bulkOrderCommission: "",
    shopImage: "",
  });

  // Fetch seller details
  useEffect(() => {
    if (user && user._id) {
      fetchSupplierData(user._id);
    } else {
      setError("No seller data found. Please login again.");
      setLoading(false);
    }
  }, [user]);

  const fetchSupplierData = async (supplierId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/supplier/suppliers-by-id/${supplierId}`
      );
      const data = res.data.data;

      setFormData({
        fullName: data.fullName || "",
        emailId: data.emailId || "",
        mobNo: data.mobNo || "",
        gender: data.gender || "",
        address: data.address || "",
        businessName: data.businessName || "",
        businessType: data.businessType || "",
        businessRegistrationName: data.businessRegistrationName || "",
        panNo: data.panNo || "",
        gstIn: data.gstIn || "",
        commission: data.commission || "",
        bulkOrderCommission: data.bulkOrderCommission || "",
        shopImage: data.shopImage || "https://via.placeholder.com/150",
      });
    } catch (err) {
      console.error("Profile Fetch Error:", err);
      setError("Unable to fetch profile data.");
    } finally {
      setLoading(false);
    }
  };

  // Save updated profile
  const handleSave = async () => {
    if (!user || !user._id) return;

    setSaving(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/supplier/update-suppliers/${user._id}`,
        formData
      );
      
      // Update local storage and context
      const updatedUser = { ...user, ...formData };
      updateUser(updatedUser);
      
      setIsEditing(false);
    } catch (err) {
      console.error("Update Error:", err);
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const onlyDate = (date) => {
    if (!date) return "";
    return date.split("-").reverse().join("-");
  };

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-gray-600 text-sm animate-pulse">
          Loading profile...
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EEF3F7] p-6">
        <div className="bg-white shadow-lg border border-red-200 rounded-lg p-6 max-w-sm text-center">
          <div className="flex justify-center mb-3">
            <BsExclamationCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Profile
          </h3>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EEF3F7] p-6">
      <div className="w-full mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex items-center gap-4">
              <img
                src={formData.shopImage}
                alt="Shop Profile"
                className="w-24 h-24 rounded-full object-cover border-4 border-blue-300 shadow-md"
              />
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    className="border rounded-md px-3 py-1 text-[0.7rem] md:text-lg mb-2"
                  />
                ) : (
                  <h1 className="text-[0.9rem] md:text-3xl font-philosopher text-primaryTextColor">
                    {formData.fullName}
                  </h1>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-gray-500 text-sm md:text-lg">{formData.businessName}</p>
                  {user?.status === "Approved" && (
                    <RiVerifiedBadgeFill className="text-green-500 w-5 h-5" />
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className={`text-xs font-medium px-3 py-1 rounded-md ${
                    user?.status === "Approved" 
                      ? "bg-green-100 text-green-700" 
                      : user?.status === "Pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-red-100 text-red-700"
                  }`}>
                    {user?.status || "Pending"}
                  </div>
                  <div className="bg-blue-100 text-blue-700 text-xs font-medium px-3 py-1 rounded-md">
                    {formData.businessType}
                  </div>
                  <div className="bg-purple-100 text-purple-700 text-xs font-medium px-3 py-1 rounded-md">
                    Seller
                  </div>
                </div>
              </div>
            </div>

            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-gradient-to-r from-[#344dc9] to-[#203abe] text-white rounded-lg shadow hover:scale-105 transition-all flex items-center gap-1"
              >
                <FaRegEdit className="w-4 h-4" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* About Section */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <IoInformationCircleOutline className="text-blue-600 w-5 h-5" />
            Personal Information
          </h2>

          {/* Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Full Name */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-blue-700 rounded-md flex items-center justify-center">
                  <CgProfile className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Full Name</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-pink-400 to-pink-700 rounded-md flex items-center justify-center">
                  <MdOutlineAlternateEmail className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Email</h3>
              </div>
              {isEditing ? (
                <input
                  type="email"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.emailId}
                  onChange={(e) =>
                    setFormData({ ...formData, emailId: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.emailId}</p>
              )}
            </div>

            {/* Phone */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-green-700 rounded-md flex items-center justify-center">
                  <LuPhone className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Phone</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.mobNo}
                  onChange={(e) =>
                    setFormData({ ...formData, mobNo: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.mobNo}</p>
              )}
            </div>

            {/* Gender */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-purple-700 rounded-md flex items-center justify-center">
                  <CgProfile className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Gender</h3>
              </div>
              {isEditing ? (
                <select
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.gender}
                  onChange={(e) =>
                    setFormData({ ...formData, gender: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <p className="text-gray-600 text-sm capitalize">
                  {formData.gender}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-orange-400 to-orange-700 rounded-md flex items-center justify-center">
                  <LuMapPin className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Address</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.address}</p>
              )}
            </div>

            {/* Registration Date */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-indigo-400 to-indigo-700 rounded-md flex items-center justify-center">
                  <SlCalender className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Registration Date</h3>
              </div>
              <p className="text-gray-600 text-sm">
                {user?.registrationDate ? onlyDate(user.registrationDate.split('T')[0]) : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <IoBusinessOutline className="text-blue-600 w-5 h-5" />
            Business Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Business Name */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-blue-700 rounded-md flex items-center justify-center">
                  <MdBusinessCenter className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Business Name</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.businessName}
                  onChange={(e) =>
                    setFormData({ ...formData, businessName: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.businessName}</p>
              )}
            </div>

            {/* Business Type */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-green-700 rounded-md flex items-center justify-center">
                  <IoBusinessOutline className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Business Type</h3>
              </div>
              {isEditing ? (
                <select
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.businessType}
                  onChange={(e) =>
                    setFormData({ ...formData, businessType: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  <option value="Wholesale">Wholesale</option>
                  <option value="Retail">Retail</option>
                  <option value="Manufacturer">Manufacturer</option>
                  <option value="Distributor">Distributor</option>
                </select>
              ) : (
                <p className="text-gray-600 text-sm">{formData.businessType}</p>
              )}
            </div>

            {/* Registration Name */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-purple-700 rounded-md flex items-center justify-center">
                  <BsFileText className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Registration Name</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.businessRegistrationName}
                  onChange={(e) =>
                    setFormData({ ...formData, businessRegistrationName: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.businessRegistrationName}</p>
              )}
            </div>

            {/* PAN Number */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-yellow-700 rounded-md flex items-center justify-center">
                  <BsFileText className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">PAN Number</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.panNo}
                  onChange={(e) =>
                    setFormData({ ...formData, panNo: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.panNo}</p>
              )}
            </div>

            {/* GSTIN */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-red-400 to-red-700 rounded-md flex items-center justify-center">
                  <BsFileText className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">GSTIN</h3>
              </div>
              {isEditing ? (
                <input
                  type="text"
                  className="border rounded-md px-2 py-1 w-full text-sm"
                  value={formData.gstIn}
                  onChange={(e) =>
                    setFormData({ ...formData, gstIn: e.target.value })
                  }
                />
              ) : (
                <p className="text-gray-600 text-sm">{formData.gstIn}</p>
              )}
            </div>

            {/* Commission */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-teal-400 to-teal-700 rounded-md flex items-center justify-center">
                  <BsPercent className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Commission</h3>
              </div>
              <p className="text-gray-600 text-sm">{formData.commission}%</p>
            </div>

            {/* Bulk Order Commission */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-cyan-700 rounded-md flex items-center justify-center">
                  <GiReceiveMoney className="text-white w-4 h-4" />
                </div>
                <h3 className="font-semibold text-gray-700 text-sm">Bulk Order Commission</h3>
              </div>
              <p className="text-gray-600 text-sm">{formData.bulkOrderCommission}%</p>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <BsFileText className="text-blue-600 w-5 h-5" />
            Documents
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {user?.shopImage && (
              <div className="text-center">
                <img src={user.shopImage} alt="Shop" className="w-32 h-32 object-cover rounded-lg mx-auto mb-2 border" />
                <p className="text-sm text-gray-600">Shop Image</p>
              </div>
            )}
            {user?.panImage && (
              <div className="text-center">
                <img src={user.panImage} alt="PAN" className="w-32 h-32 object-cover rounded-lg mx-auto mb-2 border" />
                <p className="text-sm text-gray-600">PAN Card</p>
              </div>
            )}
            {user?.gstCertificate && (
              <div className="text-center">
                <img src={user.gstCertificate} alt="GST" className="w-32 h-32 object-cover rounded-lg mx-auto mb-2 border" />
                <p className="text-sm text-gray-600">GST Certificate</p>
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        {isEditing && (
          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={() => setIsEditing(false)}
              className="px-6 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow flex items-center gap-2"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierProfile;